/**
 * Pure helper for the `displayedConversations` memo in layout/Sidebar.tsx.
 * Called inside the orchestrator's `useMemo`, so it must stay side-effect-free.
 */

import type { Conversation } from '@/types/conversations';

interface ComputeDisplayedConversationsArgs {
  tempConversationOrder: Conversation[] | null;
  userConversations: Conversation[];
  normalizedSearchQuery: string;
}

/**
 * Dedupe by `id`, first occurrence wins. A cancelled/failed report build can
 * leave a stale temp conversation behind in `tempConversationOrder`, colliding
 * with the same conversation already present elsewhere in the array — the
 * sidebar renders this list keyed on `conversation.id` (#5542), and a
 * duplicate key makes React lose child identity and drop click-driven
 * renders for every conversation below it. Returns the SAME array reference
 * when nothing was duplicated so this stays a no-op for the common case.
 */
function dedupeConversationsById(conversations: Conversation[]): Conversation[] {
  const seen = new Set<string>();
  let hasDuplicate = false;
  for (const conversation of conversations) {
    if (seen.has(conversation.id)) {
      hasDuplicate = true;
      break;
    }
    seen.add(conversation.id);
  }
  if (!hasDuplicate) return conversations;

  const kept = new Set<string>();
  const result: Conversation[] = [];
  for (const conversation of conversations) {
    if (kept.has(conversation.id)) continue;
    kept.add(conversation.id);
    result.push(conversation);
  }
  return result;
}

export function computeDisplayedConversations({
  tempConversationOrder,
  userConversations,
  normalizedSearchQuery,
}: ComputeDisplayedConversationsArgs): Conversation[] {
  const source = dedupeConversationsById(tempConversationOrder ?? userConversations);
  if (normalizedSearchQuery) {
    return source.filter((conversation) => (conversation.name || '').toLowerCase().includes(normalizedSearchQuery));
  }
  return source.slice(0, 7);
}
